/**
 * DebugPanelVM implementation manages debug panel UI state through signals.
 * It loads test states from the game registry when a game is selected, fetches
 * deployment info from a JSON endpoint, and extracts initial state names from URLs.
 * The implementation detects game start events by comparing previous and current
 * game IDs, providing a wasGameJustStarted flag for UI transitions.
 */
import { computed, effect, signal } from '@preact/signals-react'
import type { DebugPanelVM, DebugPanelVMDeps } from './debugPanelVM.types'
import { loadPositions as loadPositionsFromRegistry, type Position } from '../../debug/positions/positions'
import type { DeploymentInfo } from '../../../components/debugPanel/deployment/DeploymentInfo/DeploymentInfo'
import type { DynamicGameCatalogEntry } from '../../catalog/dynamicGameLoader/dynamicGameLoader.types'
import type { DevelopmentProgress } from '../../catalog/developmentProgress.types'

function getStateNameFromURL(): string | null {
  if (typeof window === 'undefined') return null
  // Format: /r/<roomName>/<gameId>/<stateName> (trailing slash allowed)
  const match = window.location.pathname.match(/\/r\/[^\/]+\/[^\/]+\/([^\/]+)\/?$/)
  return match ? match[1] : null
}

function getRoomNameAndGameIdFromURL(): { roomName: string | null; gameId: string | null } {
  if (typeof window === 'undefined') return { roomName: null, gameId: null }
  const pathMatch = window.location.pathname.match(/\/r\/([^\/]+)(?:\/([^\/]+)(?:\/([^\/]+))?)?\/?$/)
  return {
    roomName: pathMatch ? pathMatch[1] : null,
    gameId: pathMatch ? pathMatch[2] : null,
  }
}

function getStoredOpenState(gameId: string | null): boolean | null {
  if (typeof window === 'undefined' || !gameId) return null
  try {
    const val = window.localStorage.getItem(`merels_creator_panel_open_${gameId}`)
    if (val === 'true') return true
    if (val === 'false') return false
    return null
  } catch {
    return null
  }
}

function setStoredOpenState(gameId: string | null, open: boolean): void {
  if (typeof window === 'undefined' || !gameId) return
  try {
    window.localStorage.setItem(`merels_creator_panel_open_${gameId}`, String(open))
  } catch {
    // Ignore storage quota or security errors
  }
}

export function makeDebugPanelVM(deps: DebugPanelVMDeps): DebugPanelVM {
  const {
    roomState,
    roomConnection,
    roomActions,
    gameRegistry,
    bot,
    moveHistory,
    activeOmniMate,
    localMates,
    controllerMates,
    matesToPlayers,
    mode,
    auth,
  } = deps

  const isOpen = signal<boolean>(false)
  const deploymentInfo = signal<DeploymentInfo | null>(null)
  const positions = signal<Position[]>([])
  const testStates = positions
  const initialStateName = signal<string | null>(null)
  const wasGameJustStarted = signal<boolean>(false)
  const switchToMovesTab = signal<boolean>(false)
  const triggerCaptureScreenshot = signal<boolean>(false)
  const requestedToolId = signal<string | null>(null)
  const moveJson = signal<string>('')
  const expandedBuildMessageIds = signal<Set<string>>(new Set())
  const isPlatformDeveloper = signal(false)

  function openAndSelectTool(toolId: string): void {
    requestedToolId.value = toolId
    setIsOpen(true)
  }

  function clearRequestedTool(): void {
    requestedToolId.value = null
  }

  function setIsOpen(value: boolean): void {
    const id = gameId.value
    if (id) {
      setStoredOpenState(id, value)
    }
    isOpen.value = value
  }

  function setDeploymentInfo(info: DeploymentInfo | null): void {
    deploymentInfo.value = info
  }

  function setPositions(posList: Position[]): void {
    positions.value = posList
  }

  function setTestStates(states: Position[]): void {
    setPositions(states)
  }

  function setInitialStateName(name: string | null): void {
    initialStateName.value = name
  }

  function setWasGameJustStarted(value: boolean): void {
    wasGameJustStarted.value = value
  }

  function requestSwitchToMovesTab(): void {
    switchToMovesTab.value = true
  }

  function clearSwitchToMovesTab(): void {
    switchToMovesTab.value = false
  }

  function requestCaptureScreenshot(): void {
    triggerCaptureScreenshot.value = true
  }

  function clearCaptureScreenshot(): void {
    triggerCaptureScreenshot.value = false
  }

  function toggleBuildMessageExpanded(versionId: string): void {
    const next = new Set(expandedBuildMessageIds.value)
    if (next.has(versionId)) {
      next.delete(versionId)
    } else {
      next.add(versionId)
    }
    expandedBuildMessageIds.value = next
  }

  async function loadDeploymentInfo(): Promise<void> {
    try {
      const response = await fetch('/deployment-info.json')
      const data = response.ok ? await response.json() : null
      setDeploymentInfo(data)
    } catch {
      setDeploymentInfo(null)
    }
  }

  /** Platform administrators are the initial platform-developer audience. */
  async function loadPlatformDeveloperStatus(): Promise<void> {
    if (!auth.isLoaded.value || !auth.currentUser.value) {
      isPlatformDeveloper.value = false
      return
    }

    try {
      const headers = new Headers()
      const token = await auth.getToken()
      if (token) headers.set('Authorization', `Bearer ${token}`)
      const hostname = window.location.hostname || ''
      if (hostname.endsWith('.localhost')) {
        headers.set('x-local-worktree', hostname.split('.')[0])
      }
      const response = await fetch(`${roomConnection._workerUrl || ''}/api/creator/admin/me`, { headers })
      const data = await response.json().catch(() => ({})) as { success?: boolean; isAdministrator?: boolean }
      isPlatformDeveloper.value = response.ok && data.success === true && data.isAdministrator === true
    } catch {
      isPlatformDeveloper.value = false
    }
  }

  function loadPositions(gameId: string | null): void {
    // Positions are safe to *display* in any mode; applying them may still be constrained
    // by whatever state injection mechanism is active for the current room.
    if (gameId) {
      const posList = loadPositionsFromRegistry(gameId, gameRegistry)
      setPositions(posList)
    } else {
      setPositions([])
    }
  }

  function loadTestStates(gameId: string | null): void {
    loadPositions(gameId)
  }

  function updateInitialStateName(initialStateNameFromURL?: string | null): void {
    const stateName = initialStateNameFromURL !== undefined 
      ? initialStateNameFromURL 
      : getStateNameFromURL()
    setInitialStateName(stateName)
  }

  const isOwnedGame = computed<boolean>(() => {
    const id = gameId.value
    if (!id) return false
    if (deps.gameCatalog) {
      return deps.gameCatalog.getGame(id)?.owned === true
    }
    const entries = gameRegistry.gamesSignal?.value || []
    return entries.find((g) => g.gameId === id)?.owned === true
  })

  const developmentProgress = computed<DevelopmentProgress | null>(() => {
    const id = gameId.value
    if (!id) return null
    const buildInfo = gameRegistry.getGameBuildInfo?.(id)?.value
    if (buildInfo?.developmentProgress) {
      return buildInfo.developmentProgress
    }
    if (deps.gameCatalog) {
      return deps.gameCatalog.getGame(id)?.developmentProgress ?? null
    }
    const entries = gameRegistry.gamesSignal?.value || []
    return entries.find((g) => g.gameId === id)?.developmentProgress ?? null
  })

  // Pre-game fallback for offering Omni mode: before any game is chosen there's no
  // "owned game" to reference yet, but the room owner is still the creator testing
  // their own room - see game-start-experience.md's "start without enough mates"
  // product rule.
  const isRoomOwner = computed<boolean>(() => {
    const user = auth.currentUser.value
    return user ? roomState.ownerId.value === user.id : false
  })
  const canUseOmniMode = computed<boolean>(() => isOwnedGame.value || isRoomOwner.value)

  function detectGameStart(gameId: string | null, previousGameId: string | null): void {
    if (gameId && (!previousGameId || gameId !== previousGameId)) {
      const wasOpen = isOpen.value
      setWasGameJustStarted(true)
      const stored = getStoredOpenState(gameId)
      if (stored !== null) {
        isOpen.value = stored
      } else if (isOwnedGame.value) {
        isOpen.value = true
      } else {
        isOpen.value = false
      }
      if (wasOpen && stored === null) {
        openAndSelectTool('presets')
      }
    }
  }

  const errorText = computed(() => roomConnection.error.value || '')

  const roomNameFromURL = computed<string | undefined>(() => {
    const { roomName } = getRoomNameAndGameIdFromURL()
    return roomName || undefined
  })

  const gameId = computed<string | null>(() => {
    const fromActiveGame = roomState.activeGame.value?.gameId
    if (fromActiveGame) return fromActiveGame
    const { gameId: urlGameId } = getRoomNameAndGameIdFromURL()
    return urlGameId || null
  })

  const gameStateJson = computed(() => {
    const activeGame = roomState.activeGame.value
    return activeGame ? JSON.stringify(activeGame.gameState, null, 2) : 'No active game'
  })

  const moveHistoryEntries = computed(() => moveHistory.entries.value)

  const ourMateId = computed<string | undefined>(() => {
    // `activeOmniMate` is only meaningful in Omni mode; otherwise fall back to localMates.
    return activeOmniMate.activeLocalOmniMateId.value || localMates.localMates.value[0]?.mateId
  })

  const mates = computed(() => roomState.mates.value as any)

  const gamePlugin = computed(() => {
    const id = gameId.value
    return id ? gameRegistry.getGamePlugin(id) : null
  })

  const encodeMove = computed(() => {
    return gamePlugin.value?.encodeMove as ((move: any) => string) | undefined
  })

  const aiProgressMessages = computed(() => bot.progressMessages.value)

  const currentGameState = computed(() => roomState.activeGame.value?.gameState || null)
  const currentCtx = computed(() => roomState.activeGame.value?.ctx || null)

  const isLocalMode = computed(() => mode.isLocalMode())
  const isOmniMode = computed(() => mode.isOmniMode())
  const isSharedScreen = computed(() => Boolean(mode.computedMode.value.sharedScreen))

  // Prefer the active game's own pinned/pushed channel (room-scoped, see
  // @merels/room's GameCatalogClient) over the session-wide followed
  // channel, which only matters pre-game-start or as a fallback.
  const gamePublishChannel = computed(() => roomState.activeGame.value?.channel ?? gameRegistry.getGameChannel?.() ?? null)
  const gameBuildInfoSignal = computed(() => {
    const id = gameId.value
    return id ? gameRegistry.getGameBuildInfo?.(id) ?? null : null
  })
  const gameBuildInfo = computed(() => gameBuildInfoSignal.value?.value ?? null)
  const isGameDynamicallyLoaded = computed(() => gameBuildInfoSignal.value !== null)
  const gameChannelPreference = computed(() => roomState.gameChannelPreference.value)
  const availableBuilds = signal<DynamicGameCatalogEntry[]>([])

  async function setGameChannelPreference(channel: string): Promise<void> {
    const id = gameId.value
    if (!id) return
    await roomActions.setGameChannelPreference(id, channel)
  }

  async function refreshAvailableBuilds(): Promise<void> {
    const id = gameId.value
    if (!id || !gameRegistry.listGameVersions) {
      availableBuilds.value = []
      return
    }
    try {
      availableBuilds.value = await gameRegistry.listGameVersions(id)
    } catch (e) {
      console.error('Failed to load available builds', e)
      availableBuilds.value = []
    }
  }

  async function selectGameBuild(version: import('@merels/room').ResolvedGameVersion): Promise<void> {
    const id = gameId.value
    if (!id) return
    await roomActions.setActiveGameVersion(id, version)
  }

  const matesDebugData = computed(() => {
    const roomName = roomNameFromURL.value
    const actingAsSlotIndex = (() => {
      const game = roomState.activeGame.value
      const mateId = activeOmniMate.activeLocalOmniMateId.value
      const groups = game?.playerToMateIds
      if (!mateId || !groups) return null
      for (let i = 0; i < groups.length; i++) {
        const g = groups[i]
        if (Array.isArray(g) && g.includes(mateId)) return i
      }
      return null
    })()

    return {
      mode: {
        isLocalMode: mode.isLocalMode(),
        isOmniMode: mode.isOmniMode(),
        isSharedScreen: Boolean(mode.computedMode.value.sharedScreen),
      },
      roomInfo: {
        roomName,
        mateID: activeOmniMate.activeLocalOmniMateId.value,
        mateIDs: activeOmniMate.localOmniMateIds.value,
      },
      activeGame: roomState.activeGame.value
        ? {
            gameId: roomState.activeGame.value.gameId,
            ctx: roomState.activeGame.value.ctx,
            mateIDs: roomState.activeGame.value.playerToMateIds ? roomState.activeGame.value.playerToMateIds.flat() : [],
            mateGroups: roomState.activeGame.value.playerToMateIds,
            mateSlotMap: {}, // Derived in PlayArea; not tracked here.
            observerIDs: [], // Not tracked in ActiveGame currently.
          }
        : null,
      roomMates: roomState.mates.value as any,
      localMates: localMates.localMates.value,
      controllerMates: controllerMates.mates.value,
      assignmentPreferences: matesToPlayers.playerAssignmentPreferences.assignments.value,
      playerAssignment: {
        ourMateId: activeOmniMate.activeLocalOmniMateId.value,
        activePlayerId: (() => {
          const ctx = roomState.activeGame.value?.ctx
          if (ctx?.currentPlayer !== undefined) {
            const bgioPlayerIndex = parseInt(ctx.currentPlayer)
            if (!isNaN(bgioPlayerIndex)) {
              return roomState.getPlayerIdForSlot(bgioPlayerIndex)
            }
          }
          return undefined
        })(),
        currentPlayerId:
          actingAsSlotIndex === null
            ? undefined
            : roomState.getPlayerIdForSlot(actingAsSlotIndex) ?? String(actingAsSlotIndex),
        playerToMateIds: roomState.activeGame.value?.playerToMateIds,
        matesCount: roomState.mates.value?.length ?? 0,
      },
    }
  })

  function setMoveJson(next: string): void {
    moveJson.value = next
  }

  async function submitMove(): Promise<void> {
    try {
      const plugin = gamePlugin.value
      const trimmed = moveJson.value.trim()

      let move: any
      if (plugin?.decodeMove) {
        try {
          move = plugin.decodeMove(trimmed)
        } catch {
          move = JSON.parse(trimmed)
        }
      } else {
        move = JSON.parse(trimmed)
      }

      const mateId = ourMateId.value
      if (!mateId) return
      await roomActions.submitMove(move, mateId)
    } catch (e) {
      // Keep error handling inside the dep; UI can stay presentational.
      console.error('Invalid move format', e)
    }
  }

  function flushMates(): void {
    roomActions.flushMates()
  }

  async function applyState(state: { G?: any; ctx?: any }): Promise<boolean> {
    await roomActions.forceGameState(state)
    return true
  }

  /**
   * Opens gameId. When seatOnlyLocalMates is true and opening needs manual setup
   * (more room mates than the game's seats), avoids the ordinary multi-mate assignment
   * dialog by explicitly seating this client's own real mate(s) and starting - every
   * other mate (e.g. a partner in the room) is left unassigned, i.e. an observer.
   * Only this client's own mates can be assigned (see updateMateAssignment's
   * same-client check), so this never reassigns another client's mate.
   *
   * This never seats Omni mates itself - the reactive auto-seat effect below does
   * that uniformly regardless of which client's action created the setup, so doing
   * it here too would just race it. This function only ever has work to do when no
   * Omni mate exists anywhere (the effect has nothing to act on then) - in
   * practice, right after exiting Omni mode. If this client doesn't have enough
   * real mates to fill every slot, the room can never legally start, so this
   * cancels back to the pre-game screen rather than leaving a half-assigned setup
   * stuck (see startGameFromSetup's readiness check in @merels/room).
   *
   * seatOnlyLocalMates is a parameter, not simply `mode.isOmniMode()` read inside
   * here, because the two callers need different answers at the point they call this:
   * - openGame() (used by, e.g., picking a catalog tile) wants it only while Omni mode
   *   is *currently* active for this client (bug: a partner joins after Omni mode was
   *   turned on, then starting a game still hit the dialog, because that case never
   *   re-ran the setOmniMode() transition that applies this).
   * - setOmniMode() below wants it even when turning Omni mode *off*, when
   *   mode.isOmniMode() already reads false - otherwise exiting Omni mode solo (no
   *   other real mate to fall back to) would hit the same dialog instead of just
   *   seating this client's one real mate.
   */
  async function openGameSeatingTheseMates(gameId: string, seatOnlyLocalMates: boolean): Promise<void> {
    const result = await roomActions.openGame(gameId)
    if (!seatOnlyLocalMates || !result || typeof result !== 'object' || result.outcome !== 'setupStarted') return

    const allLocalMates = localMates.localMates.value
    const omniMates = allLocalMates.filter((m: any) => m.isOmniMate)
    if (omniMates.length > 0) return // the reactive effect below seats these

    const slotCount = result.setup?.slotCount ?? allLocalMates.length
    if (allLocalMates.length < slotCount) {
      await roomActions.cancelGameSetup()
      return
    }
    for (let slot = 0; slot < slotCount; slot++) {
      await roomActions.updateMateAssignment(allLocalMates[slot].mateId, slot)
    }
    await roomActions.startGameFromSetup()
  }

  async function openGame(gameId: string): Promise<void> {
    await openGameSeatingTheseMates(gameId, mode.isOmniMode())
  }

  /**
   * Resolves once the room's own mate list (server-confirmed, roomState.mates)
   * reflects every one of this client's current local mates. Local mate roster
   * changes (e.g. applyOmniMode()/exitOmniMode() flipping which mates
   * localMates produces) sync to the room as a fire-and-forget network call
   * (see room/init.ts, roomActions.updateLocalMates) - there is no built-in way
   * to await that specific call, so this instead waits for its observable
   * effect: the room broadcasting the updated roster back.
   */
  function waitForLocalMatesSynced(): Promise<void> {
    const isSynced = () => {
      const expectedIds = localMates.localMates.value.map((m: any) => m.mateId)
      const roomMateIds = new Set((roomState.mates.value || []).map((m: any) => m.mateId))
      return expectedIds.every((id: string) => roomMateIds.has(id))
    }
    if (isSynced()) return Promise.resolve()
    return new Promise<void>((resolve) => {
      const dispose = effect(() => {
        if (isSynced()) {
          dispose()
          resolve()
        }
      })
    })
  }

  /**
   * Toggles Omni mode for the current room (see wiki/decisions/2026-10-04-use-
   * networked-rooms-for-creator-omni-mode.md). Flipping the mode flag makes
   * localMates immediately source its roster from whichever local mate(s) that
   * direction implies (this client's Omni mates when turning on, its one real
   * mate when turning off) - the mates-sync effect in room/init.ts dispatches
   * that roster to the room, but as a fire-and-forget network call (see
   * roomActions.updateLocalMates), so this explicitly waits for the room to
   * actually confirm the new roster (waitForLocalMatesSynced) before doing
   * anything that depends on it already being there - otherwise a caller
   * opening a game right after this resolves (e.g. a catalog tile click) can
   * reach the room before the roster update does, see it as still short on
   * mates, and fail to start at all.
   *
   * If a game is active or mid-setup, it's reopened rather than resumed - the
   * room may have other mates seated from before (e.g. a partner, or this
   * client's previous single mate when turning Omni mode on), and there's no
   * seat-preserving way to swap seats mid-game.
   */
  async function setOmniMode(enabled: boolean): Promise<void> {
    if (enabled) {
      mode.applyOmniMode()
      // In a managed room the first Omni mate hosts rather than plays, so add
      // one to keep the usual two players.
      if (roomState.managed.value) {
        const omniCount = localMates.localMates.value.filter((m: any) => m.isOmniMate).length
        localMates.setOmniMateCount(Math.max(omniCount, 3))
      }
    } else {
      mode.exitOmniMode()
    }
    await waitForLocalMatesSynced()

    const gameId = roomState.activeGame.value?.gameId ?? roomState.gameSetup.value?.gameId
    if (!gameId) return

    if (roomState.activeGame.value) {
      await roomActions.endGame()
    } else {
      await roomActions.cancelGameSetup()
    }

    await openGameSeatingTheseMates(gameId, true)
  }

  async function applyOmniMode(): Promise<void> {
    await setOmniMode(true)
  }

  async function exitOmniMode(): Promise<void> {
    await setOmniMode(false)
  }

  // Auto-seat this client's Omni mates whenever room-wide setup needs manual
  // seating (assignSlots, or choosePlayerCount when the Omni mate count fits an
  // option) - regardless of which client's action (a catalog tile click, the URL
  // auto-start effect, or another connected mate opening the same game) actually
  // created the setup. This is needed because only the owning client can assign
  // its own mates (see updateMateAssignment's same-client check): a setup opened
  // by another client would otherwise sit waiting for manual arrangement forever,
  // since nobody else is allowed to seat these mates.
  //
  // seatingSetupKey guards against this effect re-entering itself: each
  // updateMateAssignment call below broadcasts a setupChanged update, which
  // re-runs this same effect while the loop is still mid-flight (not every Omni
  // mate assigned yet) - without the guard, that re-run would see
  // alreadyAssigned still false and launch a second, overlapping assignment loop
  // for the exact same setup. Keyed on the setup's own identity (not just "is one
  // in flight") so a *new* setup (this client reopening a different game) is
  // never blocked by a stale key.
  let seatingSetupKey: string | null = null
  effect(() => {
    const setup = roomState.gameSetup.value
    if (!setup) {
      seatingSetupKey = null
      return
    }
    // A host Omni mate who observes under the game's Host plays setting is not seated.
    const omniMates = localMates.localMates.value.filter((m: any) => m.isOmniMate && setup.seatEligibleMateIds.includes(m.mateId))
    if (omniMates.length === 0) return

    if (setup.phase === 'choosePlayerCount') {
      if (setup.slotCountOptions?.includes(omniMates.length)) {
        void roomActions.choosePlayerCount(omniMates.length)
      }
      return
    }

    if (setup.phase === 'assignSlots') {
      const alreadyAssigned = omniMates.every((m) => setup.assignments[m.mateId] !== undefined)
      if (alreadyAssigned) return
      const setupKey = `${setup.gameId}:${setup.startedAt}`
      if (seatingSetupKey === setupKey) return
      seatingSetupKey = setupKey
      const slotCount = setup.slotCount ?? omniMates.length
      void (async () => {
        try {
          for (let slot = 0; slot < Math.min(omniMates.length, slotCount); slot++) {
            await roomActions.updateMateAssignment(omniMates[slot].mateId, slot)
          }
          await roomActions.startGameFromSetup()
        } finally {
          seatingSetupKey = null
        }
      })()
    }
  })

  // Effects / orchestration: keep this out of React components.
  void loadDeploymentInfo()

  effect(() => {
    void auth.isLoaded.value
    void auth.currentUser.value
    void loadPlatformDeveloperStatus()
  })

  effect(() => {
    loadTestStates(gameId.value)
  })

  effect(() => {
    gameId.value // establish the dependency before the async call below
    void refreshAvailableBuilds()
  })

  if (typeof window !== 'undefined') {
    const updateStateNameFromURL = () => updateInitialStateName()
    window.addEventListener('popstate', updateStateNameFromURL)
    updateStateNameFromURL()
  }

  let previousGameId: string | null = null
  effect(() => {
    const next = gameId.value
    detectGameStart(next, previousGameId)
    previousGameId = next
  })

  effect(() => {
    const id = gameId.value
    const owned = isOwnedGame.value
    if (id && owned) {
      const stored = getStoredOpenState(id)
      if (stored === null && !isOpen.value) {
        isOpen.value = true
      }
    }
  })

  effect(() => {
    if (!wasGameJustStarted.value) return
    const timer = setTimeout(() => setWasGameJustStarted(false), 100)
    return () => clearTimeout(timer)
  })

  return {
    isOpen,
    isOwnedGame,
    canUseOmniMode,
    isPlatformDeveloper,
    developmentProgress,
    deploymentInfo,
    positions,
    testStates,
    initialStateName,
    wasGameJustStarted,
    switchToMovesTab,
    triggerCaptureScreenshot,
    setIsOpen,
    setDeploymentInfo,
    setPositions,
    setTestStates,
    setInitialStateName,
    setWasGameJustStarted,
    requestSwitchToMovesTab,
    clearSwitchToMovesTab,
    requestCaptureScreenshot,
    clearCaptureScreenshot,
    requestedToolId,
    openAndSelectTool,
    clearRequestedTool,
    loadDeploymentInfo,
    loadPlatformDeveloperStatus,
    loadPositions,
    loadTestStates,
    updateInitialStateName,
    detectGameStart,

    errorText,
    gameId,
    roomNameFromURL,
    gameStateJson,
    moveHistoryEntries,
    ourMateId,
    mates,
    encodeMove,
    moveJson,
    aiProgressMessages,
    currentGameState,
    currentCtx,
    isLocalMode,
    isOmniMode,
    isSharedScreen,
    matesDebugData,
    gamePublishChannel,
    gameBuildInfo,
    isGameDynamicallyLoaded,
    gameChannelPreference,
    availableBuilds,
    expandedBuildMessageIds,

    setMoveJson,
    submitMove,
    flushMates,
    applyState,
    openGame,
    applyOmniMode,
    exitOmniMode,
    setGameChannelPreference,
    refreshAvailableBuilds,
    selectGameBuild,
    toggleBuildMessageExpanded,
  }
}
